/**
 * @name DependentesPage
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Página principal para gestão de dependentes do responsável
 * @package components/pages/gestao-financeira/dependentes
 */

import { AttachMoney as AttachMoneyIcon, People as PeopleIcon, Refresh as RefreshIcon, SwapHoriz as SwapHorizIcon } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputAdornment, InputLabel, OutlinedInput, Typography, useMediaQuery } from '@mui/material';
import React, { useState } from 'react';
import { useThemeContext } from '../../../../contexts';
import { currencyFormatterParser } from '../../../../utils/handleChange';
import PageContainer from '../../../layouts/PageContainer';
import FormDistribuirSaldo from './components/FormDistribuirSaldo';
import FormTransferirSaldo from './components/FormTransferirSaldo';
import ListaDependentes from './components/ListaDependentes';
import ModalDependenteDetalhe from './components/ModalDependenteDetalhe';
import useDependentesData from './useDependentesData';

/**
 * Página principal de gestão de dependentes
 * Consome dados da view vw_saldos_alunos via hook database-driven
 */
const DependentesPage: React.FC = () => {
     // Estado para controlar a aba inicial do modal de detalhes
     const [initialTabDetalhes, setInitialTabDetalhes] = React.useState(0);
     const { dependentes, loading, metricas, distribuirSaldo, transferirEntreDependentes, refresh, metadados } = useDependentesData();
     const { theme } = useThemeContext();
     const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

     // Estados para modais
     const [openDistribuirModal, setOpenDistribuirModal] = useState(false);
     const [openTransferirModal, setOpenTransferirModal] = useState(false);
     const [openDetalhesModal, setOpenDetalhesModal] = useState(false);
     const [openDistribuirMassaModal, setOpenDistribuirMassaModal] = useState(false);
     const [selectedDependenteId, setSelectedDependenteId] = useState<number | null>(null);
     const [selectedDependente, setSelectedDependente] = useState<any>(null);

     // Breadcrumbs para navegação
     const breadcrumbs = metadados.breadcrumbs;

     // Ações do cabeçalho com responsividade
     const actions = (
          <Box
               sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 1 : 2,
                    width: isMobile ? '100%' : 'auto',
               }}
          >
               <Button
                    variant='outlined'
                    size={isMobile ? 'small' : 'medium'}
                    startIcon={<RefreshIcon />}
                    onClick={refresh}
                    fullWidth={isMobile}
               >
                    Atualizar
               </Button>
               <Button
                    variant='outlined'
                    size={isMobile ? 'small' : 'medium'}
                    startIcon={<AttachMoneyIcon />}
                    onClick={() => setOpenDistribuirMassaModal(true)}
                    disabled={dependentes.length === 0}
                    fullWidth={isMobile}
               >
                    Distribuir para Todos
               </Button>
               {/*onClick={() => setOpenTransferirModal(true)}
                    disabled={dependentes.length < 2*/}
               <Button
                    variant='contained'
                    size={isMobile ? 'small' : 'medium'}
                    startIcon={<SwapHorizIcon />}
                    onClick={() => setOpenTransferirModal(true)}
                    disabled={dependentes.length < 2}
                    fullWidth={isMobile}
                    color='secondary'
               >
                    Transferir Entre Dependentes
               </Button>
          </Box>
     );

     return (
          <PageContainer
               title={metadados.titulo}
               description={metadados.subtitulo}
               icon={<PeopleIcon />}
               breadcrumbs={breadcrumbs}
               actions={actions}
          >
               <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
                    {/* Cards de Métricas */}
                    <Box sx={{ flexShrink: 0 }}>
                         <Grid
                              container
                              spacing={isMobile ? 2 : 3}
                         >
                              <Grid
                                   item
                                   xs={6}
                                   sm={6}
                                   md={3}
                              >
                                   <Card sx={{ height: '100%' }}>
                                        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                                             <Typography
                                                  color='text.secondary'
                                                  gutterBottom
                                                  variant={isMobile ? 'caption' : 'body2'}
                                                  sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                                             >
                                                  Total de Dependentes
                                             </Typography>
                                             <Typography
                                                  variant={isMobile ? 'h5' : 'h4'}
                                                  sx={{ fontSize: isMobile ? '1.5rem' : '2.125rem' }}
                                             >
                                                  {metricas.totalDependentes}
                                             </Typography>
                                        </CardContent>
                                   </Card>
                              </Grid>

                              <Grid
                                   item
                                   xs={6}
                                   sm={6}
                                   md={3}
                              >
                                   <Card sx={{ height: '100%' }}>
                                        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                                             <Typography
                                                  color='text.secondary'
                                                  gutterBottom
                                                  variant={isMobile ? 'caption' : 'body2'}
                                                  sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                                             >
                                                  Meu Saldo
                                             </Typography>
                                             <Typography
                                                  variant={isMobile ? 'h6' : 'h4'}
                                                  color='primary'
                                                  sx={{
                                                       fontSize: isMobile ? '1rem' : '2.125rem',
                                                       wordBreak: 'break-word',
                                                  }}
                                             >
                                                  R$ {metricas.totalSaldoDistribuido.toFixed(2)}
                                             </Typography>
                                        </CardContent>
                                   </Card>
                              </Grid>

                              <Grid
                                   item
                                   xs={6}
                                   sm={6}
                                   md={3}
                              >
                                   <Card sx={{ height: '100%' }}>
                                        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                                             <Typography
                                                  color='text.secondary'
                                                  gutterBottom
                                                  variant={isMobile ? 'caption' : 'body2'}
                                                  sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                                             >
                                                  Dependentes Ativos
                                             </Typography>
                                             <Typography
                                                  variant={isMobile ? 'h5' : 'h4'}
                                                  color='success.main'
                                                  sx={{ fontSize: isMobile ? '1.5rem' : '2.125rem' }}
                                             >
                                                  {metricas.dependentesAtivos}
                                             </Typography>
                                        </CardContent>
                                   </Card>
                              </Grid>

                              <Grid
                                   item
                                   xs={6}
                                   sm={6}
                                   md={3}
                              >
                                   <Card sx={{ height: '100%' }}>
                                        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                                             <Typography
                                                  color='text.secondary'
                                                  gutterBottom
                                                  variant={isMobile ? 'caption' : 'body2'}
                                                  sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                                             >
                                                  Com Saldo Baixo
                                             </Typography>
                                             <Typography
                                                  variant={isMobile ? 'h5' : 'h4'}
                                                  color={metricas.dependentesComSaldoBaixo > 0 ? 'warning.main' : 'success.main'}
                                                  sx={{ fontSize: isMobile ? '1.5rem' : '2.125rem' }}
                                             >
                                                  {metricas.dependentesComSaldoBaixo}
                                             </Typography>
                                        </CardContent>
                                   </Card>
                              </Grid>
                         </Grid>
                    </Box>

                    {/* Lista de Dependentes */}
                    <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                         <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pb: 2 }}>
                              <Typography
                                   variant='h6'
                                   gutterBottom
                                   sx={{ flexShrink: 0 }}
                              >
                                   Meus Dependentes
                              </Typography>

                              <ListaDependentes
                                   dependentes={dependentes}
                                   loading={loading}
                                   onDistribuirSaldo={(idAluno) => {
                                        setSelectedDependenteId(idAluno);
                                        setOpenDistribuirModal(true);
                                   }}
                                   onTransferirSaldo={(idAluno) => {
                                        setSelectedDependenteId(idAluno);
                                        setOpenTransferirModal(true);
                                   }}
                                   onConfigurar={(idAluno) => {
                                        const dependente = dependentes.find((dep) => dep.id_aluno === idAluno);
                                        setSelectedDependente(dependente);
                                        setOpenDetalhesModal(true);
                                        // O modal será aberto na aba de configurações (aba 3)
                                        setInitialTabDetalhes(3);
                                   }}
                                   onVerDetalhes={(idAluno) => {
                                        const dependente = dependentes.find((dep) => dep.id_aluno === idAluno);
                                        setSelectedDependente(dependente);
                                        setOpenDetalhesModal(true);
                                   }}
                              />
                         </CardContent>
                    </Card>
               </Box>
               {/* Modal Distribuir Saldo */}
               <FormDistribuirSaldo
                    open={openDistribuirModal}
                    onClose={() => {
                         setOpenDistribuirModal(false);
                         setSelectedDependenteId(null);
                    }}
                    dependenteSelecionado={selectedDependenteId}
                    dependentes={dependentes}
                    onDistribuir={async (dados) => {
                         if (selectedDependenteId && dados.valor) {
                              const sucesso = await distribuirSaldo({
                                   idAluno: selectedDependenteId,
                                   valor: dados.valor,
                                   observacoes: dados.observacoes,
                              });
                              if (sucesso) {
                                   setOpenDistribuirModal(false);
                                   setSelectedDependenteId(null);
                              }
                              return sucesso;
                         }
                         return false;
                    }}
               />
               {/* Modal Transferir Saldo */}
               <FormTransferirSaldo
                    open={openTransferirModal}
                    onClose={() => {
                         setOpenTransferirModal(false);
                         setSelectedDependenteId(null);
                    }}
                    dependentes={dependentes}
                    onTransferir={async (dados) => {
                         const sucesso = await transferirEntreDependentes(dados);
                         if (sucesso) {
                              setOpenTransferirModal(false);
                              setSelectedDependenteId(null);
                         }
                         return sucesso;
                    }}
               />
               <ModalDependenteDetalhe
                    open={openDetalhesModal}
                    onClose={() => {
                         setOpenDetalhesModal(false);
                         setSelectedDependente(null);
                         setInitialTabDetalhes(0);
                    }}
                    dependente={selectedDependente}
                    initialTab={initialTabDetalhes}
               />
               {(() => {
                    const [valorMassa, setValorMassa] = React.useState<string>('');
                    const [valorMassaNumerico, setValorMassaNumerico] = React.useState<number>(0);
                    const [loadingMassa, setLoadingMassa] = React.useState(false);
                    // Para manter o estado ao abrir/fechar, usar React.useRef para guardar setters
                    const valorMassaRef = React.useRef(valorMassa);
                    valorMassaRef.current = valorMassa;
                    const handleCloseMassa = () => {
                         setOpenDistribuirMassaModal(false);
                         setValorMassa('');
                         setValorMassaNumerico(0);
                         setLoadingMassa(false);
                    };
                    const isValid = valorMassaNumerico > 0 && valorMassaNumerico <= 500 && dependentes.length > 0;
                    return (
                         <Dialog
                              open={openDistribuirMassaModal}
                              onClose={handleCloseMassa}
                              maxWidth='sm'
                              fullWidth
                              fullScreen={isMobile}
                              PaperProps={{
                                   sx: {
                                        m: isMobile ? 0 : 2,
                                        width: isMobile ? '100%' : 'auto',
                                   },
                              }}
                         >
                              <DialogTitle sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}>Distribuir Saldo para Todos os Dependentes</DialogTitle>
                              <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
                                   <Typography
                                        variant='body2'
                                        color='text.secondary'
                                        sx={{
                                             mb: 2,
                                             fontSize: isMobile ? '0.8rem' : '0.875rem',
                                        }}
                                   >
                                        Distribua o mesmo valor para todos os {dependentes.length} dependente{dependentes.length !== 1 ? 's' : ''}.
                                   </Typography>
                                   <FormControl fullWidth>
                                        <InputLabel
                                             htmlFor='valor-massa'
                                             sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                                        >
                                             Valor por Dependente
                                        </InputLabel>
                                        <OutlinedInput
                                             id='valor-massa'
                                             startAdornment={<InputAdornment position='start'>R$</InputAdornment>}
                                             label='Valor por Dependente'
                                             placeholder='0,00'
                                             size={isMobile ? 'small' : 'medium'}
                                             value={valorMassa}
                                             onChange={(e) => {
                                                  const { formatted, numeric } = currencyFormatterParser(e.target.value);
                                                  setValorMassa(formatted);
                                                  setValorMassaNumerico(numeric);
                                             }}
                                             disabled={loadingMassa}
                                             inputProps={{
                                                  inputMode: 'numeric',
                                                  pattern: '[0-9]*',
                                                  maxLength: 17,
                                             }}
                                        />
                                   </FormControl>
                                   {/* Validação do valor */}
                                   {valorMassaNumerico > 500 && (
                                        <Typography
                                             color='error'
                                             variant='caption'
                                        >
                                             O valor máximo por dependente é R$ 500,00
                                        </Typography>
                                   )}
                                   {valorMassaNumerico > 0 && valorMassaNumerico <= 500 && (
                                        <Typography
                                             color='success.main'
                                             variant='caption'
                                        >
                                             {(() => {
                                                  // Corrige casas decimais: calcula em centavos e formata
                                                  const totalCentavos = Math.round(valorMassaNumerico * 100) * dependentes.length;
                                                  const totalReais = totalCentavos / 100;
                                                  return `Total: ${currencyFormatterParser(totalReais.toFixed(2)).formatted}`;
                                             })()}
                                        </Typography>
                                   )}
                              </DialogContent>
                              <DialogActions sx={{ px: isMobile ? 2 : 3, pb: isMobile ? 2 : 2 }}>
                                   <Button
                                        onClick={handleCloseMassa}
                                        size={isMobile ? 'small' : 'medium'}
                                        disabled={loadingMassa}
                                   >
                                        Cancelar
                                   </Button>
                                   <Button
                                        variant='contained'
                                        disabled={!isValid || loadingMassa}
                                        size={isMobile ? 'small' : 'medium'}
                                        onClick={async () => {
                                             setLoadingMassa(true);
                                             try {
                                                  await Promise.all(
                                                       dependentes
                                                            .filter((dep) => typeof dep.id_aluno === 'number' && dep.id_aluno !== null)
                                                            .map((dep) =>
                                                                 distribuirSaldo({
                                                                      idAluno: dep.id_aluno as number,
                                                                      valor: valorMassaNumerico,
                                                                 }),
                                                            ),
                                                  );
                                                  handleCloseMassa();
                                             } catch (err) {
                                                  // O hook já exibe erro
                                             } finally {
                                                  setLoadingMassa(false);
                                             }
                                        }}
                                   >
                                        {loadingMassa
                                             ? 'Distribuindo...'
                                             : (() => {
                                                    const totalCentavos = Math.round(valorMassaNumerico * 100) * dependentes.length;
                                                    const totalReais = totalCentavos / 100;
                                                    return `Distribuir ${currencyFormatterParser(totalReais.toFixed(2)).formatted}`;
                                               })()}
                                   </Button>
                              </DialogActions>
                         </Dialog>
                    );
               })()}
          </PageContainer>
     );
};

export default DependentesPage;
